import { useEffect, useState } from 'react';
import { useAppSelector } from '@store';
import {
  ButtonPseudo,
  ButtonSecondary,
  ButtonSize,
  SidePanel,
  SidePanelLoader,
  SidePanelType,
} from '@ui/src';
import { AmlChangeLogsTypes } from '../types';
import AmlChangeLog from '../../../../components/common/aml/parts/change-log';
import useAmlChangeLogs from '../hooks/use-aml-change-logs';
import { ApplicationType } from '../../../new-application/types';
import { CustomerScore, OrganizationType } from '../../types';

import cn from '../styles.module.css';

const orderIsOdd = (order: number) => {
  const isOdd = order % 2;

  return Boolean(isOdd);
};

const AmlChangeLogs = ({
  panelIsOpen,
  getAmlChangeLogs,
  amlChangeLogs,
  handleCloseAmlChangeLogsPanel,
  amlChangeLogsIsLoading,
  amlScoresHistoryIsFetching,
  type,
  users,
  organizationId,
  applicationId,
  amlChangeLogsPagination,
}: AmlChangeLogsTypes) => {
  const [evenLogs, setEvenLogs] = useState<CustomerScore[]>([]);
  const [oddLogs, setOddLogs] = useState<CustomerScore[]>([]);

  const { riskScoreRanges } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const {
    onCollapse,
    isLoading,
    isFetching,
    applicationAmlDetailsIsLoading,
    applicationAmlDetailsIsFetching,
    selectedChangeLogId,
  } = useAmlChangeLogs({
    organizationId,
    applicationId,
  });

  useEffect(() => {
    if (panelIsOpen) {
      getAmlChangeLogs();
    }
  }, [panelIsOpen]);

  useEffect(() => {
    if (amlChangeLogs && amlChangeLogs?.length) {
      const evenLogsList: CustomerScore[] = [];
      const oddLogsList: CustomerScore[] = [];

      amlChangeLogs?.forEach((item) => {
        if (orderIsOdd(item.order as number)) {
          oddLogsList.push(item);
        } else {
          evenLogsList.push(item);
        }
      });

      setEvenLogs(evenLogsList);
      setOddLogs(oddLogsList);
    }
  }, [amlChangeLogs]);

  let content;

  let changeLogsType: OrganizationType | ApplicationType;

  if (
    type === OrganizationType.INDIVIDUAL ||
    type === OrganizationType.CORPORATE
  ) {
    changeLogsType =
      type === OrganizationType.INDIVIDUAL
        ? OrganizationType.INDIVIDUAL
        : OrganizationType.CORPORATE;
  } else if (
    type === ApplicationType.INDIVIDUAL ||
    type === ApplicationType.COMPANY_OFFICIAL ||
    type === ApplicationType.CORPORATE
  ) {
    changeLogsType = type;
  }

  if (
    amlChangeLogsIsLoading ||
    (amlScoresHistoryIsFetching && !amlChangeLogs)
  ) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <div>
        {oddLogs?.map((item) => (
          <AmlChangeLog
            key={item.id}
            type={changeLogsType}
            amlChangeLog={item}
            evenChangeLog={
              evenLogs?.find(
                (evenLog) => evenLog?.order === (item.order as number) + 1,
              ) || undefined
            }
            userName={
              item?.employeeId
                ? users?.find((user) => user.value === item.employeeId)?.name
                : ''
            }
            riskScoreRanges={riskScoreRanges}
            onCollapse={onCollapse}
            isLoading={isLoading}
            isFetching={isFetching}
            applicationAmlDetailsIsLoading={applicationAmlDetailsIsLoading}
            applicationAmlDetailsIsFetching={applicationAmlDetailsIsFetching}
            selectedChangeLogId={selectedChangeLogId}
          />
        ))}
        {amlChangeLogsPagination && amlChangeLogsPagination?.hasNextPage && (
          <div className={cn.showMoreButtonWrapper}>
            <ButtonSecondary
              size={ButtonSize.SMALL}
              text={amlScoresHistoryIsFetching ? 'Loading...' : 'Show more'}
              onClick={() =>
                getAmlChangeLogs(amlChangeLogsPagination.endCursor)
              }
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <SidePanel
      title="Customer Risk Score: Change log"
      isOpen={panelIsOpen}
      type={SidePanelType.Transactions}
      sidepanelExpandedKey="aml-risk-score-change-logs"
      onClose={() => {
        handleCloseAmlChangeLogsPanel();
        setEvenLogs([]);
        setOddLogs([]);
      }}
      footer={
        <ButtonPseudo
          text="Close"
          onClick={() => {
            handleCloseAmlChangeLogsPanel();
            setEvenLogs([]);
            setOddLogs([]);
          }}
          bold
        />
      }
      closeOnClickOutside
    >
      {content}
    </SidePanel>
  );
};

export default AmlChangeLogs;
